import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { IUser } from '../../UserSlice';
import { RootState } from '../..';
import {
  adminCreateGroupTutorRateCourseType,
  adminGetGroupTutorRateCourseTypes,
  adminGetGroupTutorRateCourseTypesForDropdown,
  adminUpdateGroupTutorRateCourseType,
  adminDeleteGroupTutorRateCourseType,
  adminVerifyGroupTutorRateCourseTypeDelete,
} from './adminApi';

export interface IGroupTutorRateCourseTypeGrade {
  curriculum: string;
  grade: string;
}

export interface IGroupTutorRateCourseType {
  _id: string;
  name: string;
  grades: IGroupTutorRateCourseTypeGrade[];
  createdBy: string | IUser;
  updatedBy: string | IUser;
}

export interface IGroupTutorRateCourseTypeDeleteVerificationData {
  groupTutorRateCount: number;
}

export interface IInitialGroupTutorRateCourseTypesState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminGroupTutorRateCourseTypeListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IGroupTutorRateCourseType>;
  };
  adminGroupTutorRateCourseTypeListDataForDropdown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IGroupTutorRateCourseType>;
  };
  adminGroupTutorRateCourseTypeDeleteVerificationData?: IGroupTutorRateCourseTypeDeleteVerificationData;
}

const initialState: IInitialGroupTutorRateCourseTypesState = {
  status: 'idle',
  // Admin
  adminGroupTutorRateCourseTypeListData: undefined,
  adminGroupTutorRateCourseTypeListDataForDropdown: undefined,
  adminGroupTutorRateCourseTypeDeleteVerificationData: undefined,
};

export const GroupTutorRateCourseTypesSlice = createSlice({
  initialState,
  name: 'groupTutorRateCourseTypes',
  reducers: {
    resetAdminGroupTutorRateCourseTypeListData: (state) => {
      state.adminGroupTutorRateCourseTypeListData = undefined;
    },
    resetAdminGroupTutorRateCourseTypeListDataForDropdown: (state) => {
      state.adminGroupTutorRateCourseTypeListDataForDropdown = undefined;
    },
    resetAdminGroupTutorRateCourseTypeDeleteVerificationData: (state) => {
      state.adminGroupTutorRateCourseTypeDeleteVerificationData = undefined;
    },
  },
  extraReducers(builder) {
    builder
      // Admin
      .addCase(adminGetGroupTutorRateCourseTypes.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminGroupTutorRateCourseTypeListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminGroupTutorRateCourseTypeListData = newAdminGroupTutorRateCourseTypeListData;
      })
      .addCase(
        adminGetGroupTutorRateCourseTypesForDropdown.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminGroupTutorRateCourseTypeListDataForDropdown = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminGroupTutorRateCourseTypeListDataForDropdown = newAdminGroupTutorRateCourseTypeListDataForDropdown;
        }
      )
      .addCase(
        adminCreateGroupTutorRateCourseType.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (state.adminGroupTutorRateCourseTypeListData) {
            const newAdminGroupTutorRateCourseTypeListData = {
              ...state.adminGroupTutorRateCourseTypeListData,
            };

            newAdminGroupTutorRateCourseTypeListData.list.unshift(result);

            if (newAdminGroupTutorRateCourseTypeListData.totalDocs) {
              newAdminGroupTutorRateCourseTypeListData.totalDocs += 1;
            } else {
              newAdminGroupTutorRateCourseTypeListData.totalDocs = 1;
            }

            state.adminGroupTutorRateCourseTypeListData = newAdminGroupTutorRateCourseTypeListData;
          }
        }
      )
      .addCase(
        adminUpdateGroupTutorRateCourseType.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminGroupTutorRateCourseTypeListData &&
            state.adminGroupTutorRateCourseTypeListData.list &&
            state.adminGroupTutorRateCourseTypeListData.list.length
          ) {
            const newAdminGroupTutorRateCourseTypeListData = {
              ...state.adminGroupTutorRateCourseTypeListData,
            };

            const index = newAdminGroupTutorRateCourseTypeListData.list.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newAdminGroupTutorRateCourseTypeListData.list[index] = result;
            }

            state.adminGroupTutorRateCourseTypeListData = newAdminGroupTutorRateCourseTypeListData;
          }
        }
      )
      .addCase(
        adminDeleteGroupTutorRateCourseType.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminGroupTutorRateCourseTypeListData &&
            state.adminGroupTutorRateCourseTypeListData.list &&
            state.adminGroupTutorRateCourseTypeListData.list.length
          ) {
            const newAdminGroupTutorRateCourseTypeListData = {
              ...state.adminGroupTutorRateCourseTypeListData,
            };

            newAdminGroupTutorRateCourseTypeListData.totalDocs = newAdminGroupTutorRateCourseTypeListData.totalDocs
              ? newAdminGroupTutorRateCourseTypeListData.totalDocs - 1
              : 0;

            newAdminGroupTutorRateCourseTypeListData.list = newAdminGroupTutorRateCourseTypeListData.list.filter(
              (item) => item._id !== result._id
            );

            state.adminGroupTutorRateCourseTypeListData = newAdminGroupTutorRateCourseTypeListData;
          }
        }
      )
      .addCase(
        adminVerifyGroupTutorRateCourseTypeDelete.fulfilled,
        (state, action) => {
          const result = action.payload;

          state.adminGroupTutorRateCourseTypeDeleteVerificationData = result;
        }
      );

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        // Admin
        adminCreateGroupTutorRateCourseType,
        adminGetGroupTutorRateCourseTypes,
        adminUpdateGroupTutorRateCourseType,
        adminDeleteGroupTutorRateCourseType
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isPending(
        // Admin
        adminCreateGroupTutorRateCourseType,
        adminGetGroupTutorRateCourseTypes,
        adminUpdateGroupTutorRateCourseType,
        adminDeleteGroupTutorRateCourseType
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        // Admin
        adminCreateGroupTutorRateCourseType,
        adminGetGroupTutorRateCourseTypes,
        adminUpdateGroupTutorRateCourseType,
        adminDeleteGroupTutorRateCourseType
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getGroupTutorRateCourseTypesState = (
  state: RootState
): RootState['groupTutorRateCourseTypes'] => state.groupTutorRateCourseTypes;

export const {
  resetAdminGroupTutorRateCourseTypeListData,
  resetAdminGroupTutorRateCourseTypeListDataForDropdown,
  resetAdminGroupTutorRateCourseTypeDeleteVerificationData,
} = GroupTutorRateCourseTypesSlice.actions;

export * from './adminApi';
export default GroupTutorRateCourseTypesSlice.reducer;
